import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Button, Breadcrumbs, Link as MuiLink, CircularProgress } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import ProductForm from '../../components/Products/ProductForm';
import { getData } from '../../Webservice/Webservice';
import PRODUCT_ROUTES from '../../API_Route/ProductRoute';
import { MdEdit, MdArrowBack } from "react-icons/md";

const initialFormState = {
    title: "",
    description: "",
    key_features: "",
    how_to_use: "",
    category_id: "",
    subcategory_id: "",
    upc: "",
    product_images: [],
    productVideos: [],
    unitType: "",
    unitValue: 0,
    unitName: "",
    status: "approved",
    isPublished: false,
    brand: "",
    ideal_for: "",
    applied_for: "",
    container_type: "",
    country_of_orgin: "",
    color: "",
    gst: "",
    hsn_code: "",
    codAvailable: false,
    tags: [],
    b2c_mrp: '',
    b2c_discountType: '',
    b2c_discountValue: '',
    b2c_sellingPrice: '',
    b2b_discountType: '',
    b2b_discountValue: '',
    b2b_sellingPrice: '',
    visibility: ['b2b', 'b2c'],
    isFree: false,
    thresholdAmount: '',
    autoAdd: false,
    minimumStockQuantity: '',
    length: '',
    width: '',
    height: '',
    dimensionUnit: '',
    weight: '',
    weightUnit: '',
    slug: '',
    meta_title: '',
    meta_description: '',
    badge: '',
    is_featured: false,
    ingredients: '',
    skin_type: '',
    hair_type: '',
    fragrance_notes: '',
    is_vegan: false,
    cruelty_free: false,
    organic: false,
    shipping_class: '',
    return_policy: ''
};

const ViewProduct = () => {
    const { productId } = useParams();
    const navigate = useNavigate();
    const [form, setForm] = useState(initialFormState);
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);
    const [loading, setLoading] = useState(true);

    const fetchCategories = async () => {
        try {
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            const response = await getData('categories/list?limit=1000', null, token);
            setCategories(Array.isArray(response.categories) ? response.categories : []);
        } catch (error) {
            console.error('Fetch categories failed:', error);
        }
    };

    const fetchSubcategories = async (categoryId) => {
        if (!categoryId) {
            setSubcategories([]);
            return;
        }
        try {
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            const response = await getData(`subcategories/list?limit=1000&category_id=${categoryId}`, null, token);
            setSubcategories(Array.isArray(response.subcategories) ? response.subcategories : []);
        } catch (error) {
            setSubcategories([]);
            console.error('Fetch subcategories failed:', error);
        }
    };

    const fetchProductDetails = async () => {
        setLoading(true);
        try {
            const tokenValue = sessionStorage.getItem('token');
            const token = tokenValue ? `Bearer ${tokenValue}` : '';
            const response = await getData(PRODUCT_ROUTES.GET_PRODUCT_DETAILS + productId, null, token);
            
            const productData = response.product || response.data || response;

            if (productData && productData._id) {
                let parsedDimensions = productData.dimensions;
                if (typeof parsedDimensions === 'string') {
                    try { parsedDimensions = JSON.parse(parsedDimensions); } catch (e) { parsedDimensions = {}; }
                }

                const newForm = {
                    ...initialFormState,
                    ...productData,
                    category_id: productData.category_id?._id || productData.category_id || '',
                    subcategory_id: productData.subcategory_id?._id || productData.subcategory_id || '',
                    product_images: Array.isArray(productData.product_images) ? productData.product_images : [],
                    productVideos: Array.isArray(productData.productVideos) ? productData.productVideos : [],
                    tags: (() => {
                        const rawTags = productData.tags;
                        if (Array.isArray(rawTags)) return rawTags;
                        if (typeof rawTags === 'string') return rawTags.split(',').map(t => t.trim()).filter(t => t.length > 0);
                        return [];
                    })(),
                    visibility: (() => {
                        const rawVis = productData.visibility;
                        if (Array.isArray(rawVis)) return rawVis;
                        if (typeof rawVis === 'string') return rawVis.split(',').map(v => v.trim()).filter(v => v.length > 0);
                        return ['b2b', 'b2c'];
                    })(),
                    length: parsedDimensions?.length ?? '',
                    width: parsedDimensions?.width ?? '',
                    height: parsedDimensions?.height ?? '',
                    dimensionUnit: parsedDimensions?.unit ?? '',
                    weight: parsedDimensions?.weight ?? '',
                    weightUnit: parsedDimensions?.weightUnit ?? ''
                };

                // Handle null values for controlled inputs
                Object.keys(newForm).forEach(key => {
                    if (newForm[key] === null) newForm[key] = '';
                });

                setForm(newForm);

                if (productData.category_id) {
                    fetchSubcategories(productData.category_id?._id || productData.category_id);
                }
            } else {
                console.error('Invalid product data received:', response);
                throw new Error('Could not load product details');
            }
        } catch (error) {
            console.error('Fetch product failed:', error);
            alert('Failed to fetch product details');
            navigate('/productmanagement');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCategories();
        fetchProductDetails();
    }, [productId]);

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 800, fontFamily: 'Mulish, sans-serif' }}>
                        Product Details
                    </Typography>
                    <Breadcrumbs aria-label="breadcrumb" sx={{ mt: 1 }}>
                        <MuiLink underline="hover" color="inherit" onClick={() => navigate('/productmanagement')} sx={{ cursor: 'pointer' }}>
                            Product Management
                        </MuiLink>
                        <Typography color="text.primary">View Product</Typography>
                    </Breadcrumbs>
                </Box>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button 
                        variant="contained" 
                        startIcon={<MdEdit />} 
                        onClick={() => navigate(`/productmanagement/edit/${productId}`)}
                        sx={{ background: 'rgb(255 209 2)', color: 'black', fontWeight: 700, borderRadius: '10px' }}
                    >
                        Edit Product
                    </Button>
                    <Button 
                        variant="outlined" 
                        startIcon={<MdArrowBack />} 
                        onClick={() => navigate('/productmanagement')}
                        sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 600 }}
                    >
                        Back to List
                    </Button>
                </Box>
            </Box>

            <Paper elevation={0} sx={{ p: 4, borderRadius: '16px', border: '1px solid #e5e7eb' }}>
                <ProductForm
                    form={form}
                    onFormChange={() => {}} // Disabled in view mode
                    onCancel={() => navigate('/productmanagement')}
                    onSubmit={() => {}} // Disabled in view mode
                    categories={categories}
                    subcategories={subcategories}
                    isEdit={true}
                    formMode="view"
                />
            </Paper>
        </Box>
    );
};

export default ViewProduct;
